import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { CANCEL } from "redux-saga";

import ApiError from "@mapmycustomers/shared/util/api/ApiError";

import getAppVersionInfo from "@app/util/getAppVersionInfo";

import principalVersionTracker from "./principalVersion";

export type RequestOptions = {
  // the request can change the user's own access, e.g. an org rename, so the version rise that
  // follows is their own change
  changesAccess?: boolean;
  noAuth?: boolean;
} & AxiosRequestConfig;

const getClientHeaders = () => {
  const { version } = getAppVersionInfo();
  return {
    "x-mmc-client": "WEB_2",
    // tells the server this app reloads its own permissions, so a change needn't sign it out
    "x-mmc-principal-refresh": "1",
    ...(version ? { "x-mmc-app-version": version } : {}),
  };
};

export default abstract class BaseApiService {
  private httpClient: AxiosInstance;

  constructor(httpClient: AxiosInstance) {
    this.httpClient = httpClient;
  }

  protected get(
    url: string,
    { changesAccess = false, noAuth = false, ...options }: RequestOptions = {}
  ) {
    const { cancel, config } = this.processOptions(options, { noAuth });
    return this.processResponse(this.httpClient.get(url, config), cancel, changesAccess);
  }

  protected post(
    url: string,
    data: any,
    { changesAccess = false, noAuth = false, ...options }: RequestOptions = {}
  ) {
    const { cancel, config } = this.processOptions(options, { noAuth });
    return this.processResponse(this.httpClient.post(url, data, config), cancel, changesAccess);
  }

  protected patch(
    url: string,
    data: any,
    { changesAccess = false, noAuth = false, ...options }: RequestOptions = {}
  ) {
    const { cancel, config } = this.processOptions(options, { noAuth });
    return this.processResponse(this.httpClient.patch(url, data, config), cancel, changesAccess);
  }

  protected put(
    url: string,
    data: any,
    { changesAccess = false, noAuth = false, ...options }: RequestOptions = {}
  ) {
    const { cancel, config } = this.processOptions(options, { noAuth });
    return this.processResponse(this.httpClient.put(url, data, config), cancel, changesAccess);
  }

  protected delete(
    url: string,
    { changesAccess = false, noAuth = false, ...options }: RequestOptions = {}
  ) {
    const { cancel, config } = this.processOptions(options, { noAuth });
    return this.processResponse(this.httpClient.delete(url, config), cancel, changesAccess);
  }

  protected uploadFileAsFormData(
    url: string,
    file: Blob | File,
    formData: FormData = new FormData(),
    { changesAccess = false, noAuth = false, ...options }: RequestOptions = {}
  ) {
    const { cancel, config } = this.processOptions(options, { noAuth });
    formData.append("file", file);
    config.headers = {
      ...config.headers,
      "Content-Type": "multipart/form-data",
      ...getClientHeaders(),
      "x-ms-blob-type": "BlockBlob",
    };
    // we don't limit timeout for file uploads because user can be on a slow connection,
    // but we still wanna let them to upload a file no matter what
    config.timeout = 0;
    return this.processResponse(this.httpClient.post(url, formData, config), cancel, changesAccess);
  }

  private processOptions(
    { data, ...options }: AxiosRequestConfig,
    { noAuth }: { noAuth?: boolean } = {}
  ) {
    const config: AxiosRequestConfig = { headers: {}, ...options };

    let cancel;
    if (!config.cancelToken) {
      const source = axios.CancelToken.source();
      config.cancelToken = source.token;
      cancel = source.cancel;
    }

    if (!noAuth) {
      const authToken = this.getAuthToken();
      if (authToken) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${authToken}`,
        };
      }
    }

    // add default headers
    config.headers = {
      "Content-Type": "application/json",
      // "Accept-Encoding": "gzip", -- no need to specify this header, browser will specify it for you
      "x-compression": true,
      ...getClientHeaders(),
      ...config.headers,
    };

    config.data = data;

    return { cancel, config };
  }

  private processResponse(promise: Promise<AxiosResponse>, cancel: any, changesAccess: boolean) {
    const requestId = principalVersionTracker.startRequest(changesAccess);
    const response: any = promise
      .then((reply) => {
        principalVersionTracker.endRequest(requestId, { headers: reply.headers, succeeded: true });
        return { reply };
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          principalVersionTracker.endRequest(requestId, { headers: undefined, succeeded: false });
          return {};
        }
        principalVersionTracker.endRequest(requestId, {
          headers: error.response?.headers,
          succeeded: false,
        });
        return { error: this.processError(error) };
      })
      .then(({ error, reply }: { error?: ApiError; reply?: AxiosResponse }) => {
        if (error) {
          throw error;
        }
        return reply ? reply.data : undefined;
      });
    if (cancel) {
      response[CANCEL] = cancel;
    }
    return response;
  }

  private processError(error: { response: AxiosResponse } | Error): ApiError | undefined {
    const response = (error as { response: AxiosResponse }).response;
    const status = response && response.status;
    const data = response && response.data;
    const result: ApiError =
      (data && this.errorFromData(data)) ||
      (status && this.errorFromStatus(status)) ||
      (error instanceof Error && new ApiError(error.message));

    if (result && !result.status) {
      result.status = status;
    }
    return result;
  }

  private errorFromData(data: any | string): ApiError {
    // eslint-disable-line class-methods-use-this
    if (typeof data === "string") {
      return new ApiError(data);
    }
    return new ApiError(data.message || "Server error", data.errors || data);
  }

  private errorFromStatus(status: number): ApiError {
    switch (status) {
      case 401:
        return new ApiError("Authentication failed");
      default:
        return new ApiError("Unknown error");
    }
  }

  protected abstract getAuthToken(): string | undefined;
}
